import React, {useEffect, useRef, useState} from 'react';
import Plyr from 'plyr'
import {useResetRecoilState, useSetRecoilState} from "recoil";
import playerState from "../../state/ui/playerState";
import debounce from 'lodash.debounce';

const PlyrPlayer = ({src, provider, sync, loop, ...props}) => {
    const plyr = useRef(null);
    const plyrRef = useRef(null);
    const setPlayerState = useSetRecoilState(playerState);
    const resetPlayerState = useResetRecoilState(playerState);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        plyr.current = new Plyr(plyrRef.current.firstChild, {
            vimeo: {
                byline: false,
                portrait: false,
                title: false,
                speed: true,
                transparent: false,
                autoplay: true,
                muted: true,
            },
            youtube: {
                noCookie: true,
            },
            muted: true,
            autoplay: true,
            fullscreen: {
                enabled: true,
                iosNative: true,
                container: null,
            },
            keyboard: {
                focused: false,
                global: false,
            },
            controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'captions', 'settings', 'fullscreen'],
            ratio: '16:9',
            settings: ['captions', 'quality'],
            quality: {
                default: 720,
            },
        });

        plyr.current.on('ready', e => {
            setPlayerState(old => ({
                ...old,
                quality: e.detail?.plyr?.quality || null,
                muted: e.detail?.plyr?.volume === 0 || e.detail?.plyr?.muted === true,
                fullscreen: e.detail?.plyr?.fullscreen?.active === true,
            }));
            setReady(true);
        });

        plyr.current.on('volumechange', e => {
            setPlayerState(old => ({
                ...old,
                muted: e.detail?.plyr?.volume === 0 || e.detail?.plyr?.muted === true,
            }));
        });

        const setFullscreen = fullscreen => {
            setPlayerState(old => ({
                ...old,
                fullscreen,
            }));
        };
        plyr.current.on('enterfullscreen', e => setFullscreen(true));
        plyr.current.on('exitfullscreen', e => setFullscreen(false));
        plyr.current.on('qualitychange',  e => {
            setPlayerState(old => ({
                ...old,
                selectedQuality: e?.detail?.quality || null,
            }))
        });

        window.SpacePlayer = {
            requestFullscreen: () => plyr.current.fullscreen.enter(),
            exitFullscreen: () => plyr.current.fullscreen.exit(),
            mute: () => plyr.current.decreaseVolume(1),
            unmute: () => plyr.current.increaseVolume(1),
            setQuality: (quality) => new Promise((resolve) => {
                plyr.current.quality = quality;
                resolve()
            }),
            setTrack: (track) => new Promise(resolve => resolve()),
        };


        return () => {
            plyr.current.destroy();
            delete window.SpacePlayer;

            return () => {
                resetPlayerState();
            }
        };
    }, []);

    useEffect(() => {
        if(!ready) {
            return;
        }

        if(provider === 'direct-mp4') {
            let items =  {};
            try {
                items = JSON.parse(src);
            }
            catch (e) {
                items = {720: src};
            }
            plyr.current.source = {
                type: "video",
                sources: Object.keys(items).map(quality => ({
                    size: parseInt(quality),
                    type: 'video/mp4',
                    src: items[quality],
                }))
            };

            setPlayerState(old => ({
                ...old,
                qualities: Object.keys(items).map(item => ({
                    id: item,
                    label: item+'p',
                })),
            }))
        }
        else {
            plyr.current.source = {
                type: "video",
                sources: [
                    {
                        src,
                        provider
                    }
                ]
            };

            setPlayerState(old => ({
                ...old,
                qualities: null,
                selectedQuality: null,
            }));
        }
    }, [provider, src, ready]);

    useEffect(() => {
        if(!ready) {
            return;
        }

        plyr.current.config.loop = {
            active: !!loop
        };
    }, [loop, ready]);

    useEffect(() => {
        if(!ready) {
            return;
        }

        let onPlay = undefined;

        if (sync) {
            const syncDate = +(new Date(sync));

            onPlay = debounce(() => {
                console.log('re-adjust')
                const currentTime = +(new Date);
                const actualSeconds = plyr.current.duration ? Math.min(plyr.current.duration, (currentTime - syncDate) / 1000) : (currentTime - syncDate) / 1000;

                if(actualSeconds > 0 && Math.abs((plyr.current.currentTime || 0) - actualSeconds) > 5) {
                    plyr.current.currentTime = actualSeconds;
                }
            }, 100);

            plyr.current.on("playing", onPlay);
            plyr.current.on("seeking", onPlay);
        } else {
            plyr.current.off("playing");
            plyr.current.off("seeking");
        }

        return () => {
            plyr.current && plyr.current.off("playing", onPlay);
            plyr.current && plyr.current.off("seeking", onPlay);
        };
    }, [sync, ready]);

    return (
        <div
            id="plyr"
            data-sync={!!sync}
            ref={plyrRef}
            {...props}
        >
            <video />
        </div>
    );
};

export default PlyrPlayer;
